import "./polyfill-resize-observer.ts";
import "@formatjs/intl-numberformat/polyfill";
import "@formatjs/intl-numberformat/locale-data/en";
import type { ComponentChildren } from "preact";
import { render } from "preact";
import { StrictMode } from "preact/compat";
import "~/styles/sass/main.sass";
import ReactModal from "react-modal";
import type { EventHint, ErrorEvent } from "@sentry/react";
import { isMeasurementUnits } from "@brickme/project-core/src/model/formatted-project-size.ts";
import { maxNumberOfBaseplates } from "@brickme/project-core/src/model/project-primitives.ts";
import initMonitoring from "@brickme/frontend-common/src/monitoring/init-monitoring.ts";
import { PasswordProtected } from "~/frontend-common/password-protected.tsx";
import AppContainer from "~/app/app-container.tsx";
import {
	optionalRuntimeEnv,
	optionalRuntimeEnvInt,
	requiredRuntimeEnv,
} from "~/utils/ensure-env.ts";
import packageInfo from "../package.json";
import { ClientError } from "./utils/client-error.ts";

initMonitoring({
	sentryDsn: optionalRuntimeEnv("SENTRY_DSN"),
	sentryEnvironment: optionalRuntimeEnv("SENTRY_ENVIRONMENT"),
	number: packageInfo.version,
	commitId: requiredRuntimeEnv("VERSION_ID"),
	buildTimestamp: requiredRuntimeEnv("BUILD_TIMESTAMP"),
	beforeSend(event: ErrorEvent, hint: EventHint) {
		if (
			hint.originalException &&
			hint.originalException instanceof ClientError
		) {
			return null;
		}
		return event;
	},
});

ReactModal.setAppElement("#root");

const protectedPassword = optionalRuntimeEnv("PROTECTED_PASSWORD");

type PossiblePasswordProtectedProps = {
	readonly children: ComponentChildren;
};

function PossiblePasswordProtected({
	children,
}: PossiblePasswordProtectedProps) {
	if (protectedPassword) {
		return (
			<PasswordProtected password={protectedPassword}>
				{children}
			</PasswordProtected>
		);
	}
	return <>{children}</>;
}

// Fix vh for mobile browsers. See https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
function updateViewHeight() {
	const vh = window.innerHeight * 0.01;
	document.documentElement.style.setProperty("--vh", `${vh}px`);
}
updateViewHeight();
window.addEventListener("resize", updateViewHeight);

// Measurement units
const rawMeasurementUnits = requiredRuntimeEnv("DEFAULT_MEASUREMENT_UNITS");
const defaultMeasurementUnits = isMeasurementUnits(rawMeasurementUnits)
	? rawMeasurementUnits
	: "metric";

// Google tag manager
const gtmId = optionalRuntimeEnv("GTM_ID");
const gtmServerDomain = optionalRuntimeEnv("GTM_SERVER_DOMAIN");

render(
	<StrictMode>
		<PossiblePasswordProtected>
			<AppContainer
				version={packageInfo.version}
				brandingId={requiredRuntimeEnv("BRANDING_ID")}
				url={window.location.href}
				userAgent={navigator.userAgent || navigator.vendor || "unknown"}
				gtmOptions={
					gtmId && gtmServerDomain
						? {
								id: gtmId,
								gtmServerDomain,
							}
						: undefined
				}
				siteCode={requiredRuntimeEnv("SITE_CODE")}
				gorgiasId={optionalRuntimeEnv("GORGIAS_ID")}
				mediaBaseUrl={requiredRuntimeEnv("MEDIA_BASE_URL")}
				defaultCountryCode={requiredRuntimeEnv("DEFAULT_COUNTRY_CODE")}
				ipInfoToken={requiredRuntimeEnv("IP_INFO_TOKEN")}
				platformCookieDomain={requiredRuntimeEnv("PLATFORM_COOKIE_DOMAIN")}
				defaultMeasurementUnits={defaultMeasurementUnits}
				cognitoPoolConfig={{
					id: requiredRuntimeEnv("USER_POOL_ID"),
					appClientId: requiredRuntimeEnv("USER_POOL_APP_CLIENT_ID"),
				}}
				shopifyConfig={{
					shopDomain: requiredRuntimeEnv("SHOPIFY_SHOP_DOMAIN"),
					storefrontAccessToken: requiredRuntimeEnv("STOREFRONT_ACCESS_TOKEN"),
				}}
				apiUrl={requiredRuntimeEnv("API_URL")}
				websiteUrl={requiredRuntimeEnv("WEBSITE_URL")}
				basePlateSizes={requiredRuntimeEnv("BASEPLATE_SIZES")
					.split(",")
					.map((s) => parseInt(s, 10))}
				restrictedBasePlateSizes={requiredRuntimeEnv(
					"RESTRICTED_BASEPLATE_SIZES",
				)
					.split(",")
					.filter(Boolean)
					.map((s) => parseInt(s, 10))}
				maxNumberOfSelectableBasePlates={
					optionalRuntimeEnvInt("NUM_SELECTABLE_BASEPLATES") ??
					maxNumberOfBaseplates
				}
			/>
		</PossiblePasswordProtected>
	</StrictMode>,
	document.getElementById("root")!,
);
